/**
 * Экран перевода.
 *
 * Приложение денег не переводит — оно делает перевод удобным: показывает
 * сумму, номер и банк, даёт скопировать номер в один тап и фиксирует факт.
 * Формулировка кнопки честная: «Я перевёл(а)», а не «Оплатить».
 */

import { useState } from 'react'

import { useDeclarePayment, type Timesheet } from '@/shared/api/pay'
import { formatMoney } from '@/shared/format'
import { haptic } from '@/shared/telegram'
import { Button } from '@/shared/ui/Button'
import { Sheet } from '@/shared/ui/Sheet'
import { Money } from '@/shared/ui/primitives'
import { ErrorBanner } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

export function PaymentSheet({
  open,
  timesheet,
  familyId,
  onClose,
}: {
  open: boolean
  timesheet: Timesheet
  familyId: number
  onClose: () => void
}) {
  const [confirming, setConfirming] = useState(false)

  const declare = useDeclarePayment(familyId, {
    onSuccess: () => {
      haptic('success')
      toast.success('Отмечено. Няня подтвердит получение')
      setConfirming(false)
      onClose()
    },
    onError: (error) => toast.error(error.message),
  })

  const payee = timesheet.payee
  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      haptic('success')
      toast.success(`${label} скопирован`)
    } catch {
      toast.error('Не удалось скопировать')
    }
  }

  return (
    <Sheet
      open={open}
      onClose={() => {
        setConfirming(false)
        onClose()
      }}
      title="Перевод"
      footer={
        confirming ? (
          <div className="space-y-2">
            <Button
              block
              size="lg"
              loading={declare.isPending}
              hapticKind="success"
              onClick={() => declare.mutate({ id: timesheet.id })}
            >
              Да, перевёл(а)
            </Button>
            <Button
              block
              size="lg"
              variant="ghost"
              onClick={() => setConfirming(false)}
              hapticKind="none"
            >
              Ещё нет
            </Button>
          </div>
        ) : (
          <Button block size="lg" onClick={() => setConfirming(true)}>
            Я перевёл(а)
          </Button>
        )
      }
    >
      {declare.error && <ErrorBanner message={declare.error.message} />}

      <div className="text-center">
        <p className="text-text-muted text-note">К переводу</p>
        <div className="mt-1">
          <Money value={timesheet.amount_final} currency={timesheet.currency} size="hero" />
        </div>
        <p className="text-text-muted mt-1 text-caption">{timesheet.period_title}</p>
      </div>

      {payee?.phone ? (
        <button
          type="button"
          onClick={() => void copy(payee.phone!, 'Номер')}
          className="press bg-surface-muted mt-5 flex w-full items-center justify-between rounded-[var(--radius-button)] px-4 py-3 text-left"
        >
          <span className="min-w-0">
            <span className="text-text-muted block text-caption">
              Телефон {payee.name}
              {payee.bank_hint ? ` · ${payee.bank_hint}` : ''}
            </span>
            <span className="tabular block text-lead font-semibold">{payee.phone}</span>
          </span>
          <span className="text-accent shrink-0 text-note font-medium">Копировать</span>
        </button>
      ) : (
        <p className="bg-warm-soft text-warm-ink mt-5 rounded-[var(--radius-button)] px-4 py-3 text-note leading-snug">
          {payee?.name ?? 'Няня'} пока не указала номер для переводов. Попросите
          заполнить его в профиле — это одно поле.
        </p>
      )}

      {confirming ? (
        <p className="text-text-muted mt-4 text-note leading-snug">
          Отметить перевод {formatMoney(timesheet.amount_final, timesheet.currency)}? Няня
          получит уведомление и подтвердит получение со своей стороны.
        </p>
      ) : (
        <p className="text-text-muted mt-4 text-caption leading-snug">
          Перевод делается в вашем банке. Приложение только фиксирует, что он был:
          банковскую операцию оно не видит и не проверяет.
        </p>
      )}
    </Sheet>
  )
}
